import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoDetectIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon
} from '@mui/icons-material';
import { detectFieldMappings, getConfidenceColor } from '../../utils/fieldDetection';

// Available entity fields for Person type - organized by category
const PERSON_FIELDS = [
  // Basic Information
  { id: 'firstName', label: 'First Name', required: false, description: 'Person\'s first name', category: 'Basic Information' },
  { id: 'lastName', label: 'Last Name', required: false, description: 'Person\'s last name', category: 'Basic Information' },
  { id: 'name', label: 'Full Name', required: false, description: 'Person\'s full name (if not split)', category: 'Basic Information' },
  { id: 'email', label: 'Email', required: false, description: 'Email address', category: 'Basic Information' },
  { id: 'phone', label: 'Phone', required: false, description: 'Phone number', category: 'Basic Information' },
  { id: 'organization', label: 'Organization', required: false, description: 'Current company or organization', category: 'Basic Information' },
  { id: 'role', label: 'Role/Title', required: false, description: 'Job title or position', category: 'Basic Information' },
  { id: 'linkedinUrl', label: 'LinkedIn URL', required: false, description: 'LinkedIn profile URL', category: 'Basic Information' },
  { id: 'bio', label: 'Bio/Description', required: false, description: 'Professional biography or description', category: 'Basic Information' },
  { id: 'skills', label: 'Skills', required: false, description: 'Comma-separated list of skills', category: 'Basic Information' },
  { id: 'address', label: 'Address', required: false, description: 'Physical address', category: 'Basic Information' },
  { id: 'website', label: 'Website', required: false, description: 'Personal or professional website', category: 'Basic Information' },

  // Faana Leadership Quality Scores (1-3 scale)
  { id: 'faana.leadership_quality_scores.courage_score', label: 'Courage Score', required: false, description: 'Ability to face challenges and take calculated risks (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.vulnerability_score', label: 'Vulnerability Score', required: false, description: 'Openness to feedback and admitting weaknesses (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.purpose_driven_score', label: 'Purpose-Driven Score', required: false, description: 'Alignment with mission beyond personal success (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.learning_agility_score', label: 'Learning Agility Score', required: false, description: 'Speed of acquiring and applying new knowledge (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.determination_score', label: 'Determination Score', required: false, description: 'Persistence in pursuing goals despite obstacles (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.self_awareness_score', label: 'Self-Awareness Score', required: false, description: 'Understanding of personal impact on others (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.adaptability_score', label: 'Adaptability Score', required: false, description: 'Flexibility in changing circumstances (1.0-3.0)', category: 'Leadership Quality Scores' },
  { id: 'faana.leadership_quality_scores.cross_cultural_score', label: 'Cross-Cultural Intelligence Score', required: false, description: 'Effectiveness across cultural boundaries (1.0-3.0)', category: 'Leadership Quality Scores' },

  // Faana Framework Scores (0-100 scale)
  { id: 'faana.framework_scores.holistic_health_score', label: 'Holistic Health Score', required: false, description: 'Physical, mental, and spiritual wellness (0-100)', category: 'Framework Scores' },
  { id: 'faana.framework_scores.financial_fitness_score', label: 'Financial Fitness Score', required: false, description: 'Financial acumen and resource management (0-100)', category: 'Framework Scores' },
  { id: 'faana.framework_scores.community_engagement_score', label: 'Community Engagement Score', required: false, description: 'Network strength and contribution (0-100)', category: 'Framework Scores' },
  { id: 'faana.framework_scores.leadership_strength_score', label: 'Overall Leadership Strength Score', required: false, description: 'Overall leadership effectiveness (0-100)', category: 'Framework Scores' },

  // Faana Growth Metrics
  { id: 'faana.growth_metrics.growth_trajectory', label: 'Growth Trajectory', required: false, description: 'Current development phase (Emerging, Developing, Advanced, Master)', category: 'Growth Metrics' },
  { id: 'faana.growth_metrics.growth_velocity', label: 'Growth Velocity', required: false, description: 'Rate of development (Slow, Moderate, Rapid)', category: 'Growth Metrics' },
  { id: 'faana.growth_metrics.assessment_date', label: 'Assessment Date', required: false, description: 'Date of last formal assessment (YYYY-MM-DD)', category: 'Growth Metrics' },
  { id: 'faana.growth_metrics.coach_name', label: 'Coach/Facilitator Name', required: false, description: 'Faana-certified coach conducting assessment', category: 'Growth Metrics' },

  // Faana Risk Assessment
  { id: 'faana.risk_assessment.growth_risk_index', label: 'Growth Risk Index', required: false, description: 'Overall risk to growth (0-10, higher = more risk)', category: 'Risk Assessment' },
  { id: 'faana.risk_assessment.risk_factors', label: 'Risk Factors', required: false, description: 'Comma-separated list of identified growth barriers', category: 'Risk Assessment' },

  // Legacy/Other
  { id: 'notes', label: 'Notes', required: false, description: 'Additional notes or comments', category: 'Other' },
  { id: 'tags', label: 'Tags', required: false, description: 'Comma-separated tags for categorization', category: 'Other' },
  { id: 'customField1', label: 'Custom Field 1', required: false, description: 'User-defined field', category: 'Custom Fields' },
  { id: 'customField2', label: 'Custom Field 2', required: false, description: 'User-defined field', category: 'Custom Fields' },
  { id: 'customField3', label: 'Custom Field 3', required: false, description: 'User-defined field', category: 'Custom Fields' }
];

// eslint-disable-next-line max-lines-per-function
function FieldMapper({ csvHeaders, csvData, mappings, onMappingChange }) {
  const [localMappings, setLocalMappings] = useState(mappings || {});
  const [autoDetectedMappings, setAutoDetectedMappings] = useState({});
  const [_draggedItem, setDraggedItem] = useState(null);
  const [showPreview, _setShowPreview] = useState(true);

  // Auto-detect mappings on mount or when headers change
  useEffect(() => {
    if (csvHeaders && csvHeaders.length > 0) {
      const detected = detectFieldMappings(csvHeaders);
      setAutoDetectedMappings(detected);

      // If no mappings provided, use auto-detected ones
      if (!mappings || Object.keys(mappings).length === 0) {
        setLocalMappings(detected);
        onMappingChange(detected);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvHeaders]);

  // Handle drag start
  const onDragStart = (result) => {
    setDraggedItem(result.draggableId);
  };

  // Handle drag end
  const onDragEnd = (result) => {
    setDraggedItem(null);

    if (!result.destination) {
      return;
    }

    const { source, destination } = result;

    // Handle mapping
    if (source.droppableId === 'csv-fields' && destination.droppableId.startsWith('entity-field-')) {
      const csvField = result.draggableId.replace('csv-', '');
      const entityField = destination.droppableId.replace('entity-field-', '');

      // Update mappings
      const newMappings = { ...localMappings };

      // Remove any existing mapping to this entity field
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === entityField) {
          delete newMappings[key];
        }
      });

      // Add new mapping
      newMappings[csvField] = entityField;

      setLocalMappings(newMappings);
      onMappingChange(newMappings);
    }
  };

  // Handle manual mapping via select dropdown
  const handleManualMapping = (csvField, entityField) => {
    const newMappings = { ...localMappings };

    if (!entityField || entityField === '') {
      delete newMappings[csvField];
    } else {
      // Remove any existing mapping to this entity field
      Object.keys(newMappings).forEach(key => {
        if (key !== csvField && newMappings[key] === entityField) {
          delete newMappings[key];
        }
      });

      newMappings[csvField] = entityField;
    }

    setLocalMappings(newMappings);
    onMappingChange(newMappings);
  };

  // Remove a mapping
  const removeMapping = (csvField) => {
    const newMappings = { ...localMappings };
    delete newMappings[csvField];
    setLocalMappings(newMappings);
    onMappingChange(newMappings);
  };

  // Auto-detect all mappings
  const autoDetectAll = () => {
    setLocalMappings(autoDetectedMappings);
    onMappingChange(autoDetectedMappings);
  };

  // Clear all mappings
  const clearAll = () => {
    setLocalMappings({});
    onMappingChange({});
  };

  // Get confidence for a mapping
  const getMappingConfidence = (csvField) => {
    if (!autoDetectedMappings[csvField]) return 0;
    if (!localMappings[csvField]) return 0;
    return autoDetectedMappings[csvField] === localMappings[csvField] ? 95 : 50;
  };

  // Get unmapped fields
  const unmappedCsvFields = csvHeaders.filter(h => !localMappings[h]);
  const unmappedEntityFields = PERSON_FIELDS.filter(f =>
    !Object.values(localMappings).includes(f.id)
  );

  // Count mappings
  const mappedCount = Object.keys(localMappings).length;
  const totalFields = csvHeaders.length;
  const requiredFieldsMapped = PERSON_FIELDS
    .filter(f => f.required)
    .every(f => Object.values(localMappings).includes(f.id));

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <Box>
        {/* Header with stats */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                Field Mapping
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Map CSV columns to entity fields
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                startIcon={<AutoDetectIcon />}
                onClick={autoDetectAll}
                variant="outlined"
                size="small"
              >
                Auto-Detect All
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                onClick={clearAll}
                variant="outlined"
                color="error"
                size="small"
                disabled={mappedCount === 0}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={`${mappedCount}/${totalFields} fields mapped`}
              color={mappedCount === totalFields ? 'success' : 'warning'}
              icon={mappedCount === totalFields ? <CheckIcon /> : <WarningIcon />}
            />
            {!requiredFieldsMapped && (
              <Chip
                label="Required fields not mapped"
                color="error"
                icon={<WarningIcon />}
              />
            )}
            <LinearProgress
              variant="determinate"
              value={(mappedCount / totalFields) * 100}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
          </Box>
        </Paper>

        {/* Info alert */}
        {unmappedCsvFields.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">
              {unmappedCsvFields.length} CSV column{unmappedCsvFields.length > 1 ? 's' : ''} not mapped.
              You can drag columns or use the dropdown to map them.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* CSV Fields Column */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Paper elevation={1} sx={{ p: 2, height: '600px', overflow: 'auto' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                CSV Columns ({csvHeaders.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List dense>
                {csvHeaders.map((header) => {
                  const isMapped = localMappings[header];
                  const confidence = getMappingConfidence(header);

                  return (
                    <ListItem
                      key={header}
                      sx={{
                        mb: 1,
                        backgroundColor: isMapped ? 'action.selected' : 'background.paper',
                        border: '1px solid',
                        borderColor: isMapped ? 'success.light' : 'divider',
                        borderRadius: 1
                      }}
                    >
                      <ListItemIcon>
                        {isMapped ? (
                          <CheckIcon color="success" />
                        ) : (
                          <DragIcon color="action" />
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: isMapped ? 'bold' : 'normal' }}>
                              {header}
                            </Typography>
                            {confidence > 0 && (
                              <Chip
                                label={`${confidence}%`}
                                size="small"
                                sx={{
                                  backgroundColor: getConfidenceColor(confidence),
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          isMapped ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinkIcon fontSize="small" color="success" />
                              <Typography variant="caption" color="success.main">
                                Mapped to: {PERSON_FIELDS.find(f => f.id === localMappings[header])?.label}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeMapping(header)}
                                sx={{ ml: 'auto' }}
                              >
                                <UnlinkIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                              <Select
                                value=""
                                onChange={(e) => handleManualMapping(header, e.target.value)}
                                displayEmpty
                                sx={{ fontSize: '0.875rem' }}
                              >
                                <MenuItem value="">
                                  <em>Select field to map...</em>
                                </MenuItem>
                                {unmappedEntityFields.map(field => (
                                  <MenuItem key={field.id} value={field.id}>
                                    {field.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* Entity Fields Column */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Paper elevation={1} sx={{ p: 2, height: '600px', overflow: 'auto' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Entity Fields (Hierarchical)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Group fields by category */}
              {Object.entries(
                PERSON_FIELDS.reduce((acc, field) => {
                  const category = field.category || 'Other';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(field);
                  return acc;
                }, {})
              ).map(([category, fields]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    display: 'block',
                    mb: 1
                  }}>
                    {category}
                  </Typography>
                  <List dense sx={{ pl: 1 }}>
                    {fields.map((field) => {
                      const mappedCsvField = Object.keys(localMappings).find(
                        key => localMappings[key] === field.id
                      );

                      return (
                        <Droppable droppableId={`entity-field-${field.id}`} key={field.id}>
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                mb: 1,
                                backgroundColor: mappedCsvField
                                  ? 'success.light'
                                  : snapshot.isDraggingOver
                                    ? 'action.hover'
                                    : 'background.paper',
                                border: '1px solid',
                                borderColor: mappedCsvField
                                  ? 'success.main'
                                  : snapshot.isDraggingOver
                                    ? 'primary.main'
                                    : 'divider',
                                borderRadius: 1,
                                minHeight: 50,
                                py: 0.5
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                {mappedCsvField ? (
                                  <CheckIcon color="success" fontSize="small" />
                                ) : field.required ? (
                                  <WarningIcon color="warning" fontSize="small" />
                                ) : (
                                  <InfoIcon color="action" fontSize="small" />
                                )}
                              </ListItemIcon>

                              <ListItemText
                                primaryTypographyProps={{ component: 'div' }}
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{
                                      fontWeight: mappedCsvField ? 'bold' : 'normal',
                                      fontSize: '0.825rem'
                                    }}>
                                      {field.label}
                                      {field.id.includes('faana.') && (
                                        <Typography component="span" variant="caption" color="primary" sx={{ ml: 0.5 }}>
                                          (Faana)
                                        </Typography>
                                      )}
                                    </Typography>
                                    {field.required && (
                                      <Chip label="Required" size="small" color="error" sx={{ height: 18, fontSize: '0.7rem' }} />
                                    )}
                                    <Tooltip title={field.description} arrow>
                                      <HelpIcon fontSize="small" color="action" sx={{ fontSize: '1rem' }} />
                                    </Tooltip>
                                  </Box>
                                }
                                secondary={
                                  mappedCsvField ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="success.dark">
                                        ← Mapped from: {mappedCsvField}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {field.description.length > 50 ? `${field.description.substring(0, 50)}...` : field.description}
                                    </Typography>
                                  )
                                }
                              />
                              {provided.placeholder}
                            </ListItem>
                          )}
                        </Droppable>
                      );
                    })}
                  </List>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Preview Section */}
        {showPreview && csvData && csvData.length > 0 && (
          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Mapped Data Preview
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ overflowX: 'auto' }}>
              <Grid container spacing={2}>
                {csvData.slice(0, 3).map((row, idx) => (
                  <Grid
                    key={idx}
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Row {idx + 1}
                      </Typography>
                      {Object.entries(localMappings).map(([csvField, entityField]) => {
                        const field = PERSON_FIELDS.find(f => f.id === entityField);
                        return (
                          <Box key={csvField} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {field?.label}:
                            </Typography>
                            <Typography variant="body2">
                              {row[csvField] || '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        )}
      </Box>
    </DragDropContext>
  );
}

export default FieldMapper;